/**
 * PERMISSIONS SOURCE OF TRUTH
 *
 * This file defines the complete role-based access control matrix for the app.
 * It is the single source of truth consumed by:
 *   - Firestore security rules (validated via tests)
 *   - Storage security rules (validated via tests)
 *   - Admin UI permissions dashboard (display)
 *   - Live verification (runtime checks)
 *
 * IMPORTANT: When modifying permissions here, you MUST also update:
 *   1. firestore.rules — to enforce the new permissions
 *   2. storage.rules — if storage paths are affected
 *   3. tests/ — to validate rules match this file
 *   4. Run `npm run test:rules` to verify
 */

// ============================================
// ROLES
// ============================================
export const ROLES = {
  admin: {
    label: 'Administrator',
    description: 'Full system access. Can manage users, prompts, settings, and all data.',
  },
  pm: {
    label: 'Project Manager',
    description: 'Full read/write access to all operational and admin data.',
  },
  tech: {
    label: 'Technician',
    description: 'Read access to operational data. Write access limited to field work: receipts, lock changes, inspections, and AI consults.',
  },
  demo: {
    label: 'Demo User',
    description: 'Restricted read-only access. Cannot see sensitive data (cards, lock codes, bank charges). Cannot write to any collection.',
  },
};

// ============================================
// GOD MODE
// ============================================
export const GOD_MODE_EMAILS = ['aj@snshn.co'];

// ============================================
// FIRESTORE COLLECTIONS
// ============================================

// Collection categories for UI grouping
export const COLLECTION_CATEGORIES = {
  operational: {
    label: 'Operational Data',
    description: 'Work orders, labor, and related operational records',
  },
  fieldWork: {
    label: 'Field Work',
    description: 'Data created by technicians in the field',
  },
  sensitive: {
    label: 'Sensitive Data',
    description: 'Financial and security-related information',
  },
  ai: {
    label: 'AI & Prompts',
    description: 'AI prompt management and usage analytics',
  },
  admin: {
    label: 'Administration',
    description: 'User management and system settings',
  },
  reference: {
    label: 'Reference Data',
    description: 'Properties, units, and technician directory',
  },
};

/**
 * Complete Firestore permissions matrix.
 *
 * Each collection defines:
 *   - category: Which COLLECTION_CATEGORIES group it belongs to
 *   - label: Human-readable name
 *   - access: Per-role { read: boolean, write: boolean }
 *
 * Rules:
 *   - admin/pm: full read/write everywhere
 *   - tech: read most, write only field work collections
 *   - demo: restricted reads (no sensitive data), no writes anywhere
 *   - GOD_MODE: bypasses all rules (enforced at rules level)
 */
export const FIRESTORE_PERMISSIONS = {
  // --- Operational Data ---
  work_orders: {
    category: 'operational',
    label: 'Work Orders',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: false },
      demo:  { read: true, write: false },
    },
  },
  labor_entries: {
    category: 'operational',
    label: 'Labor Entries',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: false },
      demo:  { read: true, write: false },
    },
  },
  surveys: {
    category: 'operational',
    label: 'Surveys',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: false },
      demo:  { read: true, write: false },
    },
  },
  inventory: {
    category: 'operational',
    label: 'Inventory',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: false },
      demo:  { read: true, write: false },
    },
  },

  // --- Field Work (tech-writable) ---
  receipts: {
    category: 'fieldWork',
    label: 'Receipts',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: true },
      demo:  { read: true, write: false },
    },
  },
  lock_changes: {
    category: 'fieldWork',
    label: 'Lock Changes',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: true },
      demo:  { read: false, write: false },
    },
  },
  inspections: {
    category: 'fieldWork',
    label: 'Inspections',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: true },
      demo:  { read: true, write: false },
    },
  },
  unit_details: {
    category: 'fieldWork',
    label: 'Unit Details',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: true },
      demo:  { read: false, write: false },
    },
  },
  unit_detail_changes: {
    category: 'fieldWork',
    label: 'Unit Detail Changes',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: true },
      demo:  { read: false, write: false },
    },
  },
  consult_sessions: {
    category: 'fieldWork',
    label: 'AI Consult Sessions',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: true },
      demo:  { read: true, write: false },
    },
  },

  // --- Sensitive Data (demo blocked from reads) ---
  cards: {
    category: 'sensitive',
    label: 'Payment Cards',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: false },
      demo:  { read: false, write: false },
    },
  },
  lock_codes: {
    category: 'sensitive',
    label: 'Lock Codes',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: false },
      demo:  { read: false, write: false },
    },
  },
  bank_charges: {
    category: 'sensitive',
    label: 'Bank Charges',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: false },
      demo:  { read: false, write: false },
    },
  },

  // --- Reference Data ---
  properties: {
    category: 'reference',
    label: 'Properties',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: false },
      demo:  { read: true, write: false },
    },
  },
  units: {
    category: 'reference',
    label: 'Units',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: false },
      demo:  { read: true, write: false },
    },
  },
  technicians: {
    category: 'reference',
    label: 'Technicians',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: false },
      demo:  { read: true, write: false },
    },
  },

  // --- AI & Prompts ---
  ai_prompts: {
    category: 'ai',
    label: 'AI Prompts',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: false },
      tech:  { read: true, write: false },
      demo:  { read: false, write: false },
    },
  },
  ai_prompt_versions: {
    category: 'ai',
    label: 'Prompt Versions',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: false },
      tech:  { read: false, write: false },
      demo:  { read: false, write: false },
    },
  },
  prompt_usage_logs: {
    category: 'ai',
    label: 'Prompt Usage Logs',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: false, write: true },
      demo:  { read: false, write: false },
    },
  },
  prompt_feedback: {
    category: 'ai',
    label: 'Prompt Feedback',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: false, write: true },
      demo:  { read: false, write: false },
    },
  },
  clarity_themes: {
    category: 'ai',
    label: 'Clarity Themes',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: false },
      tech:  { read: false, write: false },
      demo:  { read: false, write: false },
    },
  },
  insurance_estimates: {
    category: 'ai',
    label: 'Insurance Estimates',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: true },
      demo:  { read: true, write: false },
    },
  },

  // --- Administration ---
  users: {
    category: 'admin',
    label: 'Users',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: false },
      tech:  { read: true, write: false }, // Can read own profile, rules enforce own-doc write
      demo:  { read: true, write: false },
    },
  },
  admin_whitelist: {
    category: 'admin',
    label: 'Admin Whitelist (Legacy)',
    access: {
      admin: { read: true, write: true },
      pm:    { read: false, write: false },
      tech:  { read: false, write: false },
      demo:  { read: false, write: false },
    },
  },
};

// ============================================
// STORAGE PATHS
// ============================================

/**
 * Storage path permissions.
 * Defines which roles can read/write to each storage path prefix.
 */
export const STORAGE_PERMISSIONS = {
  'receipts/': {
    label: 'Receipt Photos',
    category: 'fieldWork',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: true },
      demo:  { read: true, write: false },
    },
  },
  'locks/': {
    label: 'Lock Photos',
    category: 'fieldWork',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: true },
      demo:  { read: false, write: false },
    },
  },
  'inspections/': {
    label: 'Inspection Photos',
    category: 'fieldWork',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: true },
      demo:  { read: true, write: false },
    },
  },
  'estimates/': {
    label: 'Estimate Documents',
    category: 'ai',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: true },
      tech:  { read: true, write: true },
      demo:  { read: true, write: false },
    },
  },
  'data_imports/': {
    label: 'Data Import Files',
    category: 'admin',
    access: {
      admin: { read: true, write: true },
      pm:    { read: true, write: false },
      tech:  { read: false, write: false },
      demo:  { read: false, write: false },
    },
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get all collections grouped by category
 */
export const getCollectionsByCategory = () => {
  const grouped = {};
  for (const [collectionId, config] of Object.entries(FIRESTORE_PERMISSIONS)) {
    const cat = config.category;
    if (!grouped[cat]) {
      grouped[cat] = {
        ...COLLECTION_CATEGORIES[cat],
        collections: [],
      };
    }
    grouped[cat].collections.push({ id: collectionId, ...config });
  }
  return grouped;
};

/**
 * Get all storage paths grouped by category
 */
export const getStoragePathsByCategory = () => {
  const grouped = {};
  for (const [path, config] of Object.entries(STORAGE_PERMISSIONS)) {
    const cat = config.category;
    if (!grouped[cat]) {
      grouped[cat] = {
        ...COLLECTION_CATEGORIES[cat],
        paths: [],
      };
    }
    grouped[cat].paths.push({ path, ...config });
  }
  return grouped;
};

/**
 * Check if a role has access to a collection
 * @param {string} role - User role
 * @param {string} collectionId - Firestore collection name
 * @param {string} action - 'read' or 'write'
 * @returns {boolean}
 */
export const hasAccess = (role, collectionId, action) => {
  const collection = FIRESTORE_PERMISSIONS[collectionId];
  if (!collection) return false;
  const roleAccess = collection.access[role];
  if (!roleAccess) return false;
  return roleAccess[action] === true;
};

/**
 * Check if a role has access to a storage path
 * @param {string} role - User role
 * @param {string} storagePath - Storage path (e.g., 'receipts/WO-123/photo.png')
 * @param {string} action - 'read' or 'write'
 * @returns {boolean}
 */
export const hasStorageAccess = (role, storagePath, action) => {
  for (const [prefix, config] of Object.entries(STORAGE_PERMISSIONS)) {
    if (storagePath.startsWith(prefix)) {
      const roleAccess = config.access[role];
      if (!roleAccess) return false;
      return roleAccess[action] === true;
    }
  }
  // No matching path prefix — deny by default
  return false;
};

/**
 * Get a summary of what a role can do (for display)
 * @param {string} role
 * @returns {{ canRead: string[], canWrite: string[], blocked: string[] }}
 */
export const getRoleSummary = (role) => {
  const canRead = [];
  const canWrite = [];
  const blocked = [];

  for (const [collectionId, config] of Object.entries(FIRESTORE_PERMISSIONS)) {
    const access = config.access[role];
    if (!access || (!access.read && !access.write)) {
      blocked.push(config.label);
    } else {
      if (access.read) canRead.push(config.label);
      if (access.write) canWrite.push(config.label);
    }
  }

  return { canRead, canWrite, blocked };
};
